import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { ModalPortal, Text } from "@livechat/design-system-react-components";

import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { config } from "config/config";
import { selectLicenseId } from "store/entities/license";
import { IReplyAddress } from "store/entities/reply-addresses/interfaces";
import { selectIsDeletingReplyAddress } from "store/entities/reply-addresses/selectors";
import { StackingContextLevel } from "styles/stacking-context-level";

interface IDeleteAddressModalProps {
  replyAddress: IReplyAddress | null;
  fromName: string | null;
  onClose(): void;
  onDelete(): void;
}

const DeleteAddressModal: FC<IDeleteAddressModalProps> = ({ replyAddress, fromName, onClose, onDelete }) => {
  const licenseId = useSelector(selectLicenseId);
  const fallbackEmail = `${licenseId}@${config.defaultMailboxSuffix}`;
  const isDeleting = useSelector(selectIsDeletingReplyAddress);
  const [prevIsDeleting, setPrevIsDeleting] = useState(isDeleting);

  useEffect(() => {
    if (prevIsDeleting && !isDeleting) {
      onClose();
    }
    setPrevIsDeleting(isDeleting);
  }, [isDeleting, prevIsDeleting]);

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <ConfirmActionModal
        heading="Delete reply address?"
        onClose={onClose}
        primaryAction={{
          label: "Delete",
          onClick: onDelete,
          actionType: "destructive",
          loading: isDeleting,
        }}
        secondaryAction={{
          label: "Cancel",
          onClick: onClose,
        }}
      >
        <Text>
          The address <strong>{replyAddress?.email || fallbackEmail}</strong> with display name{" "}
          <strong>{fromName}</strong> is assigned to several teams.
        </Text>
        <Text>
          By deleting it, these teams will get the default address <strong>{fallbackEmail}</strong>. Are you sure?
        </Text>
      </ConfirmActionModal>
    </ModalPortal>
  );
};

export default DeleteAddressModal;
